import { Injectable, inject } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    isFormArray,
    UntypedFormArray,
    UntypedFormControl,
} from '@angular/forms';
import {
    Action,
    ConditionalMessage,
    FieldDefinition,
    FormContext,
    FormDefinition,
    FormElementLayoutDefinition,
    FormElementType,
    FormFieldType,
    FormFunctionResult,
    FormInitialisationMode,
    FormLock,
    FormSectionLayoutDefinition,
    Function as WdxFunction,
    INITIALISATION_MODE_MAP,
    MifidConcat,
    MifidConcatDataDate,
    Trigger,
    UpdateField,
    UpdateLabel,
    UpdateSection,
    WdxDestroyClass,
    FieldCondition,
    FieldDefault,
    FieldRule,
} from '@wdx/shared/utils';
import {
    BehaviorSubject,
    combineLatest,
    debounceTime,
    delay,
    filter,
    forkJoin,
    map,
    Observable,
    of,
    startWith,
    Subject,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';

import {
    FormControlService,
    FormLockService,
    FormPendingChangesService,
    FormProcessConditionDataService,
    FormStaticService,
    FormTriggersService,
    FormValidationService,
} from '../../services';

import { IFormDataResult, IFormPendingChange } from '../../constants';
import {
    IFormControlData,
    IReactiveStore,
    LayoutAndDefinitionEntry,
    LayoutAndFieldDefinition,
    ReactiveFormElement,
    ReactiveFormElementChild,
    ReactiveFormLayoutAndDefinition,
} from '../../models';
import { FormFieldsDataService } from '../form-fields-data';
import * as R from 'ramda';
import { getFormControlType } from '../../utils';
import { FieldConditionService } from './form-field-condition.service';
import { ADDRESS_CONTROL_ADDRESS_TYPE } from '../../components/form-controls/form-address-control/services/address-type.service';
import { FormApiFunctionService } from '../form-api-function/form-api-function.service';

export const FORM_CONFIG_CHANGE_DEBOUNCE_TIME = 100;

type UpdateFieldConfig = UpdateField &
    Pick<FieldDefinition, 'childSchema'> & {
        visibilityLocked?: boolean;
        visibilityLockOverride?: boolean;
        mandatoryLocked?: boolean;
        mandatoryLockOverride?: boolean;
        correlationId?: number;
        unHiding?: boolean;
    };

@Injectable()
export class FormConditionsService extends WdxDestroyClass {
    private _layoutAndDefinition$ = new BehaviorSubject<
        LayoutAndDefinitionEntry[]
    >([]);
    private formSectionLayoutDefinitions!: FormSectionLayoutDefinition[];
    private pristine = true;
    private form!: FormGroup;
    private formData!: IFormDataResult;
    private formDefinition!: FormDefinition;
    private formId!: string;
    private fieldsToNullCheck!: string[];
    private conditionFunctions: any[] = [];
    private conditionFunctions$: {
        run$: Observable<FormFunctionResult>;
        functionName: string;
    }[] = [];
    private initialisationMode!: FormInitialisationMode;
    private isChildInstance = false;
    private subformValueChangeAlwaysEmit = false;

    private formProcessService = inject(FormProcessConditionDataService);
    private formTriggersService = inject(FormTriggersService);
    private formLockService = inject(FormLockService);
    private formControlService = inject(FormControlService);
    private formFieldsDataService = inject(FormFieldsDataService);
    private formValidationService = inject(FormValidationService);
    private formStaticService = inject(FormStaticService);
    private fieldConditionService = inject(FieldConditionService);
    private formPendingChangesService = inject(FormPendingChangesService);
    private formApiFunctionService = inject(FormApiFunctionService);

    private updateFieldCtr = 0;

    fieldValueUpdated$ = new Subject<string>();

    private isDestroyed = false;
    private debugConditions = false;

    get layoutAndDefinition$(): Observable<LayoutAndDefinitionEntry[]> {
        return this._layoutAndDefinition$.asObservable().pipe(
            debounceTime(FORM_CONFIG_CHANGE_DEBOUNCE_TIME),
            // clone is important - it prevents any form fields mutating the form state
            map((layoutAndDefinition) => structuredClone(layoutAndDefinition)),
        );
    }

    get layoutAndDefinition(): LayoutAndDefinitionEntry[] {
        return structuredClone(this._layoutAndDefinition$.value);
    }

    // for performance reasons this is not cloned - not make this public or use in any public methods that may return this mutable object
    private get unsafeLayoutAndDefinition(): LayoutAndDefinitionEntry[] {
        return this._layoutAndDefinition$.value;
    }

    constructor() {
        super();
        this.destroyed$.pipe(take(1)).subscribe(() => {
            this.isDestroyed = true;
        });
        this.debugConditions =
            localStorage.getItem('wdx-debug-formframework-conditions') ===
            'true';
    }

    resetFormSectionLayoutDefinitions(): void {
        this.layoutAndDefinitionNext$(
            structuredClone(this.layoutAndDefinition),
        );
    }

    configService(
        form: FormGroup,
        formDefinition: FormDefinition,
        formData: IFormDataResult,
        formId: string,
        fieldsToNullCheck: string[],
        initialisationMode: FormInitialisationMode,
        extraConfig?: {
            isChildInstance?: boolean;
            subformValueChangeAlwaysEmit?: boolean;
        },
    ): void {
        if (
            localStorage.getItem('wdx-debug-formframework-log-state') === 'true'
        ) {
            this.layoutAndDefinition$
                .pipe(
                    takeUntil(this.destroyed$),
                    filter(
                        (value) =>
                            Boolean(this.formDefinition?.name) &&
                            Boolean(value),
                    ),
                    tap((value) =>
                        // do not remove - this is an intentional logger for debugging purposes
                        console.info(this.formDefinition.name, value),
                    ),
                )
                .subscribe();
        }

        this.form = form;
        this.formData = formData;
        this.formDefinition = structuredClone(formDefinition);
        this.formId = formId;
        this.formSectionLayoutDefinitions = [
            ...(this.formDefinition?.layout?.sectionLayoutDefinitions || []),
        ];
        this.fieldsToNullCheck = fieldsToNullCheck;
        this.initialisationMode = initialisationMode;
        this.isChildInstance = extraConfig?.isChildInstance ?? false;
        this.subformValueChangeAlwaysEmit =
            extraConfig?.subformValueChangeAlwaysEmit ?? false;

        this.combinedLayoutAndDefinition();
        if (!this.isChildInstance) {
            this.applyTriggerCondition();
            this.applyConcat();
        }
    }

    setupPendingChanges(
        formBuilderFirstLevel: any,
        pendingChanges: IFormPendingChange[] | undefined | null,
    ) {
        const updated = this.formPendingChangesService.setUpPendingChanges(
            this.layoutAndDefinition,
            formBuilderFirstLevel,
            pendingChanges,
        );
        if (updated) {
            this.layoutAndDefinitionNext$(
                updated as LayoutAndDefinitionEntry[],
            );
        }
    }

    setFormControlListener(
        trigger: Trigger,
        formControlData: IFormControlData,
    ): void {
        this.formControlService.setValueChangeListener(
            trigger,
            formControlData,
            this.applyConditions.bind(this),
            this.destroyed$,
        );
    }

    applyIsTrigger(
        trigger: Trigger,
        triggerFieldValue: any,
        formControlData?: IFormControlData,
    ): void {
        trigger.is?.forEach((is: any) => {
            const conditionIsTrue = this.formTriggersService.is(
                is,
                triggerFieldValue,
            );
            const actionSource: any = conditionIsTrue ? is.then : is.else;

            this.updateSectionFromArray(actionSource);
            this.updateMessageDefinitionToShow(
                actionSource?.hideMessage,
                actionSource?.showMessage,
            );

            actionSource?.updateFields?.forEach((updateField: any) => {
                this.updateField(updateField, formControlData?.index, {
                    trigger,
                });
            });

            actionSource?.updateLabels?.forEach((updateLabel: any) =>
                this.updateLabel(updateLabel),
            );

            actionSource?.functions?.forEach((updateLabel: any) =>
                this.executeFunction(updateLabel, trigger, formControlData),
            );
        });
    }

    reset() {
        this.layoutAndDefinitionNext$([]);
        this.formSectionLayoutDefinitions = [];
        this.pristine = true;
        this.conditionFunctions = [];
        this.conditionFunctions$ = [];
    }

    /**
     * Sets the isLocked status on definition fields
     * @param forceFullFormLock
     */
    updateLockStatuses(forceFullFormLock: boolean): void {
        const layoutAndDefinition = this.layoutAndDefinition.map(
            (formElement) => {
                return {
                    ...formElement,
                    layoutAndDefinition: formElement.layoutAndDefinition?.map(
                        (definition) => {
                            return {
                                ...definition,
                                isLocked:
                                    forceFullFormLock ||
                                    this.formLockService.fieldIsLocked(
                                        definition.name as string,
                                        this.formData.lock as FormLock,
                                        false,
                                    ),
                            };
                        },
                    ),
                };
            },
        );
        this.layoutAndDefinitionNext$(layoutAndDefinition);
    }

    updateFunctionChanges(
        functionRes: FormFunctionResult,
        functionName: string,
    ): void {
        if (functionRes?.dataChanges) {
            if (this.debugConditions) {
                console.log(
                    `form function ${functionName} updates: `,
                    functionRes,
                );
            }
            Object.keys(functionRes?.dataChanges).map((item) => {
                const NAME = this.formControlService.stringToFormControlObject(
                    item as string,
                );

                let formControl = this.form.get(NAME.Level1ControlName);

                if (typeof NAME.Level1SubFormIndex === 'number') {
                    formControl =
                        this.formControlService.getFormArrayIndexFormControl(
                            formControl as FormArray,
                            NAME.Level1SubFormIndex,
                            NAME.Level2ControlName as string,
                        );
                }

                if (!formControl) {
                    return;
                }

                const controlIsArray = formControl instanceof FormArray;
                const preventEmitter =
                    !this.subformValueChangeAlwaysEmit && controlIsArray
                        ? true
                        : undefined;
                const data = functionRes.dataChanges?.[item];
                this.pristine = false;
                formControl.markAsTouched();
                formControl.markAsDirty();
                this.formProcessService.updateFormControlValue(
                    formControl as UntypedFormControl,
                    data,
                    this.pristine,
                    preventEmitter,
                );
            });
        }

        if (functionRes?.schemaChanges) {
            Object.keys(functionRes.schemaChanges ?? {}).forEach(
                (propertyName) => {
                    const FIELD_DATA =
                        functionRes.schemaChanges?.[propertyName];
                    if (FIELD_DATA) {
                        this.updateField(FIELD_DATA as UpdateField, undefined, {
                            functionName,
                        });
                    }
                },
            );
        }
    }

    public updateControlIsDisabled(
        formControl: AbstractControl,
        isDisabled: boolean,
        source: string,
    ) {
        const { path } =
            this.formControlService.getMetadataFromFormControl(formControl);

        this.updateField({ name: path, isDisabled }, undefined, { source });
    }

    public disableFormGroupChildValidators(formGroup: FormGroup) {
        Object.values(formGroup.controls).forEach((control) => {
            control.clearValidators();
            control.updateValueAndValidity();
        });
    }

    public enableFormGroupChildValidators(formGroup: FormGroup) {
        Object.values(formGroup.controls).forEach((control) => {
            const config = this.getLayoutAndDefinitionFromFormControl(control);
            this.formProcessService.updateFormControlValidation(
                control as UntypedFormControl,
                new FieldDefinition(config as FieldDefinition),
                new FormElementLayoutDefinition({
                    config,
                    elementType: FormElementType.Field,
                } as any),
            );
        });
    }

    private getLayoutAndDefinitionFromFormControl(
        formControl: AbstractControl,
    ): ReactiveFormLayoutAndDefinition | ReactiveFormElementChild {
        const { nameHierarchy } =
            this.formControlService.getMetadataFromFormControl(formControl);
        let layoutAndDefinition:
            | ReactiveFormElementChild
            | ReactiveFormLayoutAndDefinition
            | ReactiveFormLayoutAndDefinition[] =
            this.unsafeLayoutAndDefinition.flatMap(
                ({ layoutAndDefinition }) => layoutAndDefinition,
            ) as unknown as ReactiveFormLayoutAndDefinition;
        nameHierarchy
            .slice(this.isChildInstance ? 2 : 1)
            .forEach((pathPart, _, array) => {
                if (typeof pathPart === 'number') {
                    layoutAndDefinition = (
                        (layoutAndDefinition as ReactiveFormLayoutAndDefinition)
                            ?.children as ReactiveFormElementChild[]
                    )?.[0]?.layoutAndDefinition?.[
                        pathPart
                    ] as ReactiveFormLayoutAndDefinition[];
                } else {
                    if (
                        (layoutAndDefinition as ReactiveFormLayoutAndDefinition)
                            ?.name !== R.last(array)
                    ) {
                        if (Array.isArray(layoutAndDefinition)) {
                            layoutAndDefinition = layoutAndDefinition?.find(
                                (y) => y.name === pathPart,
                            ) as ReactiveFormLayoutAndDefinition;
                        } else {
                            const possibleMatchedElement = (
                                (
                                    (
                                        layoutAndDefinition as ReactiveFormElementChild
                                    )
                                        ?.layoutAndDefinition as ReactiveFormLayoutAndDefinition[][]
                                )?.[0] as ReactiveFormLayoutAndDefinition[]
                            )?.find((y) => y.name === pathPart);
                            if (!possibleMatchedElement) {
                                layoutAndDefinition =
                                    layoutAndDefinition?.children?.find(
                                        (y) => y.name === pathPart,
                                    ) as ReactiveFormElementChild;
                            } else {
                                layoutAndDefinition = possibleMatchedElement;
                            }
                        }
                    }
                }
            });
        if (
            layoutAndDefinition &&
            layoutAndDefinition.name !== R.last(nameHierarchy)
        ) {
            console.error(
                'mismatch detected when finding config from formControl instance',
                R.last(nameHierarchy),
                this.isChildInstance,
            );
        }
        return layoutAndDefinition;
    }

    public addArrayItem(
        formElement: ReactiveFormLayoutAndDefinition,
        childItems: ReactiveFormLayoutAndDefinition[],
    ) {
        if (formElement.elementType !== FormElementType.Array) {
            throw new Error('addArrayItem called on non-array formElement');
        }
        const updatedLayoutAndDefinition = this.layoutAndDefinition;
        const eld = updatedLayoutAndDefinition
            .flatMap(({ layoutAndDefinition }) => layoutAndDefinition)
            ?.find((element) => element?.name === formElement.name)
            ?.children?.[0]?.layoutAndDefinition;
        if (!Array.isArray(eld)) {
            throw new Error(
                'failed to find target when adding a new array item to form',
            );
        }
        (eld as ReactiveFormLayoutAndDefinition[][]).push(childItems);
        this.layoutAndDefinitionNext$(updatedLayoutAndDefinition);
    }

    public removeArrayItem(
        formElement: ReactiveFormLayoutAndDefinition,
        indexToRemove: number,
    ) {
        if (formElement.elementType !== FormElementType.Array) {
            throw new Error('removeArrayItem called on non-array formElement');
        }
        const updatedLayoutAndDefinition = this.layoutAndDefinition;
        const child = updatedLayoutAndDefinition
            .flatMap(({ layoutAndDefinition }) => layoutAndDefinition)
            ?.find((element) => element?.name === formElement.name)
            ?.children?.[0];
        if (!Array.isArray(child?.layoutAndDefinition)) {
            throw new Error(
                'failed to find target when removing an array item from form',
            );
        }
        child.layoutAndDefinition = (
            child!.layoutAndDefinition as ReactiveFormLayoutAndDefinition[][]
        ).filter((_, i) => i !== indexToRemove);
        this.layoutAndDefinitionNext$(updatedLayoutAndDefinition);
    }

    public updateControlChildren(
        formElement: ReactiveFormLayoutAndDefinition,
        formControl: AbstractControl,
        children?: ReactiveFormElement[],
    ) {
        const { nameHierarchy } =
            this.formControlService.getMetadataFromFormControl(formControl);

        const path = nameHierarchy.slice(1);
        const updatedLayoutAndDefinition = this.layoutAndDefinition;
        let element: ReactiveFormLayoutAndDefinition | undefined =
            updatedLayoutAndDefinition
                .flatMap(({ layoutAndDefinition }) => layoutAndDefinition)
                .find((element) => element?.name === path[0]);
        if (typeof path[1] === 'number') {
            element = (
                element?.children?.[0]?.layoutAndDefinition?.[
                    path[1]
                ] as unknown as ReactiveFormLayoutAndDefinition[]
            )?.find((element) => element?.name === path[2]);
        }
        if (element?.name === formElement.name && element?.children) {
            element.children = children as ReactiveFormElement[];
            this.layoutAndDefinitionNext$(updatedLayoutAndDefinition);
        } else {
            throw new Error(
                'failed to find target when adding a new array item to form',
            );
        }
    }

    public externallyTriggerFunctions(
        functionNames: string[],
        subFormName?: string,
        subFormIndex?: number,
    ) {
        functionNames.forEach((functionName) => {
            this.formProcessService
                .getApiFunctionTrigger(
                    {
                        formId: this.formId,
                        formValue: this.formFieldsDataService.parseFormData(
                            this.fieldsToNullCheck,
                            this.form,
                        ),
                    },
                    functionName,
                    subFormName,
                    subFormIndex,
                )

                .pipe(
                    filter((res) => Boolean(res)),
                    take(1),
                )
                .subscribe((data: FormFunctionResult) => {
                    this.updateFunctionChanges(data, functionName);
                });
        });
    }

    public setDefaultFieldValues(
        fieldDefaults: FieldDefault[] | null | undefined,
        subformIndex?: number,
    ) {
        fieldDefaults?.forEach(({ field: target, value: val }) => {
            if (!target || R.isNil(val)) {
                return;
            }
            const subformIndices =
                typeof subformIndex === 'number' ? [subformIndex] : undefined;
            let targetValue = this.formControlService.getFormControlData(
                this.form,
                target,
                subformIndices,
            )[0]?.formControl?.value;

            const targetParts = target?.split('.');
            if (targetParts?.length === 3) {
                targetValue = targetValue?.[targetParts[2]];
            } else if (targetParts?.length > 3) {
                console.warn(
                    'Cannot apply a default condition more than 3 levels deep!, target:',
                    target,
                );
                return;
            }

            // In Add mode we want to allow overridding the form value even if it exists.
            // In Edit mode we do not update if the target has a value - we're setting a default if there is
            // no current value, we're not overriding any existing values
            if (
                this.formStaticService.initialisationMode ===
                    FormInitialisationMode.Edit &&
                R.isNotNil(targetValue)
            ) {
                return;
            }

            let value: string | boolean = val;
            if (val?.toString()?.startsWith('@')) {
                value = this.formControlService.getFormControlData(
                    this.form,
                    val.slice(1),
                    subformIndices,
                )[0]?.formControl?.value;
            } else if (['true', 'false'].includes(val.toLowerCase())) {
                const fieldType =
                    this.formStaticService.getFieldDefinitionForFieldFromFormDef(
                        target,
                    )?.fieldType;
                if (fieldType === FormFieldType.Boolean) {
                    value = val.toLowerCase() === 'true';
                } else if (fieldType === FormFieldType.Select) {
                    const isSelectWithBooleans = this.formStaticService
                        .getFieldDefinitionForFieldFromFormDef(target)
                        ?.options?.some(
                            (option) => typeof option.value === 'boolean',
                        );
                    if (isSelectWithBooleans) {
                        value = val.toLowerCase() === 'true';
                    }
                }
            }
            const formControlData =
                typeof subformIndex === 'number'
                    ? { index: subformIndex }
                    : undefined;
            this.updateField(
                {
                    value,
                    name: target,
                },
                formControlData?.index,
                { source: 'default' },
            );
        });
    }

    public updateFormVisibilityAndListeners(
        visibilityRules?: FieldRule[] | null,
        subformIndex?: number,
    ) {
        visibilityRules?.forEach(({ conditions, field }) => {
            if (!field || !conditions) {
                return;
            }
            if (R.isNil(subformIndex) && field.includes('.')) {
                const subforms = this.formControlService.getFormControlData(
                    this.form,
                    field as string,
                );
                subforms.forEach((_, i) => {
                    this.updateFormVisibilityAndListenerForControl(
                        conditions,
                        field,
                        i,
                    );
                });
                return;
            }
            this.updateFormVisibilityAndListenerForControl(
                conditions,
                field,
                subformIndex,
            );
        });
    }

    public updateFormMandatoryAndListeners(
        mandatoryRules?: FieldRule[] | null,
        subformIndex?: number,
    ) {
        mandatoryRules?.forEach(({ conditions, field }) => {
            if (!field || !conditions) {
                return;
            }
            if (R.isNil(subformIndex) && field.includes('.')) {
                const subforms = this.formControlService.getFormControlData(
                    this.form,
                    field as string,
                );
                subforms.forEach((_, i) => {
                    this.updateFormManadatoryAndListenerForControl(
                        conditions,
                        field,
                        i,
                    );
                });
                return;
            }
            this.updateFormManadatoryAndListenerForControl(
                conditions,
                field,
                subformIndex,
            );
        });
    }

    public formHasAnyValues(formValue: any): boolean {
        return Object.values(formValue).some(
            (value) => R.isNotNil(value) && R.isNotEmpty(value),
        );
    }

    /**
     * Gets layout and def for a field path e.g. 'field1', 'field1.subfield1', 'field1[0].subfield1'
     *
     * For subforms if a index is given (e.g. familyMembers[0] or familyMembers[0].person) the subform instance will be used
     * When no index is provided the childSchema will be used, e.g. familyMembers.person would provide the childSchema and not an
     * isntance of a subform
     *
     * Important - this is the combined layout and defintion that includes changes from from conditions and functions
     *
     * @param fieldPath
     * @param definition
     * @returns
     */
    public getCurrentConfigForFieldPath(
        fieldPath: string,
    ):
        | ReactiveFormLayoutAndDefinition
        | ReactiveFormLayoutAndDefinition[]
        | ReactiveFormElementChild
        | FieldDefinition
        | null
        | undefined {
        const pathParts = this.formControlService.stringToFormControlObject(
            fieldPath as string,
        );
        const layoutAndDefinition = this.unsafeLayoutAndDefinition;
        if (!pathParts.Level1ControlName) {
            return;
        }

        const firstLevel = layoutAndDefinition
            .find((section) =>
                section.layoutAndDefinition?.some(
                    ({ name }) => name === pathParts.Level1ControlName,
                ),
            )
            ?.layoutAndDefinition?.find(
                ({ name }) => name === pathParts.Level1ControlName,
            );

        if (!firstLevel) {
            return;
        }

        let subformInstance;
        if (R.isNotNil(pathParts.Level1SubFormIndex)) {
            subformInstance = firstLevel?.children?.[0]?.layoutAndDefinition?.[
                pathParts.Level1SubFormIndex
            ] as ReactiveFormLayoutAndDefinition[];
        }

        if (!pathParts.Level1HasSubForm) {
            return R.isNil(pathParts.Level1SubFormIndex)
                ? structuredClone(firstLevel)
                : structuredClone(subformInstance);
        }

        if (
            !pathParts.Level2ControlName &&
            R.isNotNil(pathParts.Level1SubFormIndex)
        ) {
            return subformInstance
                ? structuredClone(subformInstance)
                : undefined;
        }

        if (
            R.isNotNil(pathParts.Level1SubFormIndex) &&
            !pathParts.Level2HasSubForm
        ) {
            const subformInstanceElement = subformInstance?.find(
                ({ name }) => name === pathParts.Level2ControlName,
            );
            return structuredClone(subformInstanceElement);
        }

        if (!pathParts.Level2HasSubForm) {
            const subformChildSchemaElement = firstLevel.childSchema?.find(
                ({ name }) => name === pathParts.Level2ControlName,
            );
            return subformChildSchemaElement
                ? structuredClone(subformChildSchemaElement)
                : undefined;
        }

        // can be implemented if required - would currently only be for address fields
        throw new Error('Third level not supported!');
    }

    public setupAddressTriggers(
        addressFieldName: string,
        formGroup: FormGroup,
    ) {
        const { path } =
            this.formControlService.getMetadataFromFormControl(formGroup);
        const pathWithoutSubformIndex = path?.replaceAll(/\[\d*]/g, '');
        const relatedTriggers =
            this.formTriggersService.findTriggersThatUpdateField(
                this.formDefinition.conditions?.triggers,
                pathWithoutSubformIndex ?? addressFieldName,
                true,
            );

        this.applyTriggers(relatedTriggers);
    }

    public applyConditions(
        trigger: Trigger,
        formControlData: IFormControlData,
    ): void {
        if (trigger?.itemAdded || trigger?.itemRemoved) {
            const functions =
                trigger.itemAdded?.functions ?? trigger.itemRemoved?.functions;
            functions?.forEach((func) => {
                this.executeFunction(func, trigger, formControlData);
            });
        }
        if (formControlData.formControl) {
            const formControl = formControlData.formControl;
            this.applyIsTrigger(trigger, formControl.value, formControlData);

            if (trigger.changed && !this.pristine && !formControl?.pristine) {
                trigger.changed?.updateFields?.forEach((field) => {
                    const FIELD = this.updateValueFromOtherFields(field);
                    this.updateField(FIELD, formControlData.index, { trigger });
                });

                trigger.changed?.recalculate?.forEach((field) => {
                    this.recalculateField(field, formControlData.index);
                });

                trigger.changed?.functions?.forEach((func) => {
                    this.executeFunction(func, trigger, formControlData);
                });
            } else if (trigger.changed && this.pristine) {
                trigger.changed?.functions?.forEach((func) => {
                    this.updateFunctionArray(func);
                });
            }
        }
    }

    private updateFormVisibilityAndListenerForControl(
        conditions: FieldCondition[],
        field: string,
        subformIndex?: number,
    ) {
        this.updateVisibility(conditions, field, subformIndex);
        const inputFormControls = conditions.map(
            (cond) =>
                this.formControlService.getFormControlData(
                    this.form,
                    cond.field as string,
                    subformIndex ? [subformIndex] : undefined,
                )[0],
        );

        if (inputFormControls?.some(Boolean)) {
            this.formControlService.setFieldConditionValueChangeListener(
                conditions,
                inputFormControls,
                field,
                this.updateVisibility.bind(this),
                this.destroyed$,
                subformIndex,
            );
        }
    }

    private updateFormManadatoryAndListenerForControl(
        conditions: FieldCondition[],
        field: string,
        subformIndex?: number,
    ) {
        this.updateMandatory(conditions, field, subformIndex);
        const inputFormControls = conditions.map(
            (cond) =>
                this.formControlService.getFormControlData(
                    this.form,
                    cond.field as string,
                    subformIndex ? [subformIndex] : undefined,
                )[0],
        );

        if (inputFormControls?.some(Boolean)) {
            this.formControlService.setFieldConditionValueChangeListener(
                conditions,
                inputFormControls,
                field,
                this.updateMandatory.bind(this),
                this.destroyed$,
                subformIndex,
            );
        }
    }

    private combinedLayoutAndDefinition(): void {
        let combinedLayoutAndDef: LayoutAndDefinitionEntry[] = [];
        this.formSectionLayoutDefinitions.forEach(
            (sectionLayoutDefinition: FormSectionLayoutDefinition) => {
                const FORM_ELEMENT: ReactiveFormElement = {
                    section: sectionLayoutDefinition,
                    layoutAndDefinition: [],
                };
                let children: any[] = [];
                let ARR: IReactiveStore = {};

                sectionLayoutDefinition.elementLayoutDefinitions?.forEach(
                    (element: FormElementLayoutDefinition) => {
                        children = [];

                        const FORM_CONTROLS_DATA =
                            this.formControlService.getFormControlData(
                                this.form,
                                element.name as string,
                            );

                        const DEFINITION: FormElementLayoutDefinition = {
                            ...this.findDefinitionFromLayoutFieldElement(
                                element,
                                this.formDefinition,
                            ),
                        };

                        for (const FORM_CONTROL_DATA of FORM_CONTROLS_DATA) {
                            this.formProcessService.updateValidation(
                                FORM_CONTROL_DATA.formControl as FormControl,
                                {
                                    ...(DEFINITION || {}),
                                    ...(element || {}),
                                } as LayoutAndFieldDefinition,
                            );
                        }

                        if (element.elementType === `${FormFieldType.Array}`) {
                            const ARR_RES =
                                this.loopSubFormLayoutAndDefinitionData(
                                    element,
                                    DEFINITION as LayoutAndFieldDefinition,
                                    [element.name as string],
                                );

                            ARR = ARR_RES;

                            if (Object.keys(ARR).length) {
                                children = [ARR];
                            }
                        }

                        const minMax = this.formValidationService.getMinAndMax(
                            DEFINITION,
                            element,
                        );
                        FORM_ELEMENT.layoutAndDefinition!.push({
                            ...DEFINITION,
                            ...element,
                            ...((DEFINITION as any).options && {
                                originalOptions: (DEFINITION as any).options,
                            }),
                            children,
                            ...minMax,
                        });
                    },
                );

                const isHidden =
                    FORM_ELEMENT.section?.isHidden ||
                    (!FORM_ELEMENT.section?.customContent &&
                        FORM_ELEMENT.layoutAndDefinition?.every((def) =>
                            Boolean(def.summaryLevel),
                        ))
                        ? { isHidden: true }
                        : {};

                combinedLayoutAndDef = [
                    ...combinedLayoutAndDef,
                    {
                        elementType: FormElementType.Field,
                        ...FORM_ELEMENT,
                        section: {
                            ...FORM_ELEMENT.section,
                            ...isHidden,
                        },
                    },
                ];
            },
        );
        this.layoutAndDefinitionNext$(combinedLayoutAndDef);
    }

    private applyConcat(): void {
        const CONCATS = this.formDefinition.conditions?.calculations?.filter(
            (cond) => Boolean(cond.equalTo?.concat),
        );
        CONCATS?.forEach(({ field, equalTo }) => {
            if (!equalTo?.concat) {
                return;
            }
            const { country, dateOfBirth, firstName, lastName } =
                equalTo.concat;

            const countryNestedValue = country?.split('.')[1];
            const controls = [
                country?.split('.')[0],
                dateOfBirth,
                firstName,
                lastName,
            ].flatMap(
                (field) =>
                    this.formControlService.getFormControlData(
                        this.form,
                        field as string,
                    )[0].formControl,
            );

            const observables = controls.map((control) =>
                control.valueChanges.pipe(startWith(control.value)),
            );

            combineLatest(observables)
                .pipe(takeUntil(this.destroyed$), debounceTime(0))
                .subscribe(([countries, dateOfBirth, firstName, lastName]) => {
                    const countryCodes =
                        countryNestedValue && Array.isArray(countries)
                            ? countries.map((c) => c[countryNestedValue])
                            : [countries];
                    const [year, month, day] = dateOfBirth?.split('-') || [];
                    const dob: MifidConcatDataDate = {
                        year,
                        month,
                        day,
                    };
                    const value = MifidConcat.generateConcatIdentifier({
                        countryCodes,
                        dob,
                        firstName,
                        lastName,
                    });

                    const formControl = this.form.get(
                        field as string,
                    ) as UntypedFormControl;

                    this.formProcessService.updateFormControlValue(
                        formControl,
                        value,
                        true,
                        true,
                    );
                });
        });
    }

    private applyTriggerCondition(): void {
        const initialisation: Action | undefined =
            this.formDefinition.conditions?.initialisation;
        const initialisationByMode =
            this.formDefinition.conditions?.initialisationByMode;

        if (initialisation) {
            this.applyActions(initialisation);
        }

        if (
            initialisationByMode &&
            (initialisationByMode as any)[
                INITIALISATION_MODE_MAP[this.initialisationMode]
            ]
        ) {
            this.applyActions(
                (initialisationByMode as any)[
                    INITIALISATION_MODE_MAP[this.initialisationMode]
                ],
            );
        }

        this.callInitialisationFunctions();

        /**
         * Trigger fields can either be of context or data sources.
         * - 'context' fields are prefixed with the '$' symbol and values are read directly from context source.
         * - 'data' field values are read from the associated formControl.
         */

        this.applyTriggers(this.formDefinition.conditions?.triggers);

        this.updateFormVisibilityAndListeners(
            this.formDefinition.conditions?.visibility,
        );

        this.updateFormMandatoryAndListeners(
            this.formDefinition.conditions?.mandatory,
        );

        this.setDefaultFieldValues(this.formDefinition.conditions?.defaults);
    }

    private applyTriggers(triggers?: Trigger[] | null | undefined) {
        triggers?.forEach((trigger) => {
            const useContext = this.formTriggersService.useContext(trigger);

            const triggerTriggers = Object.keys(trigger).filter(
                (key) => key !== 'field',
            );
            if (
                triggerTriggers.every((t) =>
                    ['itemAdded', 'itemRemoved'].includes(t),
                )
            ) {
                // if we only have itemAdded or itemRemoved then skip applying now and setting up listeners
                // these triggers are invoked by the FormArrayService
                return;
            }

            if (useContext) {
                const { context } = this.formData;

                const contextValue =
                    this.formTriggersService.getContextTriggerValue(
                        trigger,
                        context as FormContext,
                    );

                if (context) {
                    this.applyIsTrigger(trigger, contextValue);
                }
            }

            if (!useContext) {
                const formControlsData =
                    this.formControlService.getFormControlData(
                        this.form,
                        trigger.field as string,
                    );

                for (const formControlData of formControlsData) {
                    this.setFormControlListener(trigger, formControlData);
                    this.applyConditions(trigger, formControlData);
                }
            }

            this.pristine = false;
        });
    }

    private updateVisibility(
        conditions: FieldCondition[],
        target: string,
        subformIndex?: number,
    ): void {
        conditions.map((cond) => {
            const formControlData = this.formControlService.getFormControlData(
                this.form,
                cond.field as string,
                subformIndex ? [subformIndex] : undefined,
            );
            const formControl = formControlData[0]?.formControl;
            const isHidden = !this.fieldConditionService.conditionTrue(
                cond,
                formControl?.value,
            );
            this.updateField(
                {
                    isHidden,
                    name: target,
                    visibilityLocked: true,
                    visibilityLockOverride: true,
                },
                subformIndex,
                {
                    source: 'visibility',
                },
            );
        });
    }

    private updateMandatory(
        conditions: FieldCondition[],
        target: string,
        subformIndex?: number,
    ): void {
        conditions.map((cond) => {
            const formControlData = this.formControlService.getFormControlData(
                this.form,
                cond.field as string,
                subformIndex ? [subformIndex] : undefined,
            );
            const formControl = formControlData[0]?.formControl;
            const isRequired = this.fieldConditionService.conditionTrue(
                cond,
                formControl?.value,
            );
            this.updateField(
                {
                    isRequired,
                    name: target,
                    mandatoryLocked: true,
                    mandatoryLockOverride: true,
                },
                subformIndex,
                {
                    source: 'mandatory',
                },
            );
        });
    }

    private executeFunction(
        field: WdxFunction,
        trigger: Trigger,
        formControlData?: IFormControlData,
    ): void {
        const { Level1ControlName, Level1HasSubForm } =
            this.formControlService.stringToFormControlObject(
                trigger.field as string,
            );
        const isSubform =
            Level1HasSubForm || isFormArray(formControlData?.formControl);

        if (formControlData) {
            this.formApiFunctionService.queueFunction(
                this.formId,
                this.fieldsToNullCheck,
                this.form,
                field.name as string,
                this.updateFunctionChanges.bind(this),
                Level1ControlName,
                formControlData?.index,
                isSubform,
            );
        }
    }

    private findDefinitionFromLayoutFieldElement(
        element: FormElementLayoutDefinition,
        formDefinition: FormDefinition,
    ): FormElementLayoutDefinition {
        const MAX_COUNT = formDefinition.schema?.length || 0;
        let name: string | undefined = '';
        let count = 0;
        let definition: FormElementLayoutDefinition = {
            name: '',
            elementType: FormElementType.Field,
        };

        while (element.name !== name && count < MAX_COUNT) {
            const definitionElement = formDefinition.schema?.[count];
            if (definitionElement?.name === element.name) {
                name = definitionElement?.name;
                definition = definitionElement as FormElementLayoutDefinition;
                count = MAX_COUNT;
            }

            count++;
        }

        return definition;
    }

    private loopSubFormLayoutAndDefinitionData(
        elementLayout: FormElementLayoutDefinition,
        definition: FieldDefinition,
        subFormPath: string[],
    ): IReactiveStore {
        const STORE_DATA: IReactiveStore = {
            section: { name: '' },
            layoutAndDefinition: [],
        };
        const ARR: any[] = [];
        const SUB_FORM_ARRAY: any[] = [];

        const FORM_ARRAY = this.formProcessService.getFormArray(
            this.form,
            subFormPath,
            0,
        );

        // pass in FieldDefinition for subform array as we need to calculate appropriate min/max for schema and/or layout
        this.formProcessService.updateValidation(
            FORM_ARRAY as UntypedFormArray,
            elementLayout as LayoutAndFieldDefinition,
            definition,
        );

        elementLayout?.sectionLayoutDefinitions?.forEach((childSection) => {
            STORE_DATA.section = childSection;

            childSection.elementLayoutDefinitions?.forEach((childElement) => {
                if (childElement.elementType === `${FormFieldType.Array}`) {
                    let childDefinition = {};
                    const updatedSubFormPath = [...subFormPath];
                    definition.childSchema?.forEach((item) => {
                        if (childElement.name === item.name) {
                            updatedSubFormPath.push(
                                childElement.name as string,
                            );
                            childDefinition = item;
                        }
                    });

                    ARR.push(
                        this.loopSubFormLayoutAndDefinitionData(
                            childElement,
                            childDefinition,
                            updatedSubFormPath,
                        ),
                    );
                }

                FORM_ARRAY?.controls?.forEach((formControl) => {
                    const elementSchema =
                        definition.childSchema?.find(
                            (elementSchema) =>
                                elementSchema.name === childElement.name,
                        ) ?? {};
                    const FORM_CONTROL = formControl.get(
                        childElement.name as string,
                    ) as UntypedFormControl;

                    this.formProcessService.updateValidation(FORM_CONTROL, {
                        ...elementSchema,
                        ...childElement,
                    } as LayoutAndFieldDefinition);
                });

                const MAX_COUNT = definition.childSchema?.length || 0;
                let name = '';
                let count = 0;
                let definitionChild = {};

                while (childElement.name !== name && count < MAX_COUNT) {
                    const definitionElement = definition.childSchema?.[count];

                    if (definitionElement?.name === childElement.name) {
                        name = definitionElement?.name as string;
                        definitionChild = definitionElement as FieldDefinition;
                    }

                    count++;
                }

                SUB_FORM_ARRAY.push(
                    JSON.parse(
                        JSON.stringify({
                            ...definitionChild,
                            ...childElement,
                            ...((definitionChild as any).options && {
                                originalOptions: (definitionChild as any)
                                    .options,
                            }),
                            children: [...ARR],
                        }),
                    ),
                );
            });
        });

        STORE_DATA.layoutAndDefinition = [];
        FORM_ARRAY?.controls?.map(() => {
            STORE_DATA.layoutAndDefinition?.push(
                JSON.parse(JSON.stringify(SUB_FORM_ARRAY)),
            );
        });

        return STORE_DATA;
    }

    private updateFunctionArray(action: any): void {
        if (!this.conditionFunctions.some((e: any) => e.name === action.name)) {
            this.conditionFunctions.push(action);
            this.conditionFunctions$.push({
                run$: this.formProcessService.getApiFunctionTrigger(
                    {
                        formId: this.formId,
                        formValue: this.formFieldsDataService.parseFormData(
                            this.fieldsToNullCheck,
                            this.form,
                        ),
                    },
                    action.name,
                ),
                functionName: action.name,
            });
        }
    }

    private applyActions(data: Action): void {
        Object.keys(data).map((actions) => {
            const ACTIONS = data[actions as keyof Action];
            if (ACTIONS?.length) {
                ACTIONS.map((action: any) => {
                    if (actions === 'functions') {
                        this.updateFunctionArray(action);
                    }

                    if (actions === 'recalculate') {
                        this.recalculateField(action);
                    }

                    if (actions === 'updateFields') {
                        const FIELD = this.updateValueFromOtherFields(action);
                        this.updateField(FIELD, undefined, { action });
                    }

                    if (actions === 'updateSections') {
                        this.updateSection(action);
                    }

                    if (actions === 'showMessage') {
                        this.updateMessageDefinitionToShow(
                            undefined,
                            ACTIONS as ConditionalMessage[],
                        );
                    }

                    if (actions === 'hideMessage') {
                        this.updateMessageDefinitionToShow(
                            ACTIONS as ConditionalMessage[],
                            undefined,
                        );
                    }
                });
            }
        });
    }

    private callInitialisationFunctions(): void {
        if (this.conditionFunctions.length && this.pristine) {
            this.pristine = false;
            const allFuncs: Record<string, Observable<FormFunctionResult>> = {};
            this.conditionFunctions$.forEach(({ run$ }, i) => {
                allFuncs[i] = run$;
            });
            forkJoin(allFuncs)
                .pipe(
                    filter((res) => Boolean(res)),
                    take(1),
                )
                .subscribe((results) => {
                    Object.entries(results).forEach(([key, value]) => {
                        this.updateFunctionChanges(
                            value,
                            this.conditionFunctions$[parseInt(key)]
                                ?.functionName,
                        );
                    });
                });
        }
    }

    private recalculateField(field: string, subformIndex?: number): void {
        const controlNameObject =
            this.formControlService.stringToFormControlObject(field);

        if (controlNameObject.Level1HasSubForm) {
            const formControl = this.form?.get(
                controlNameObject.Level1ControlName,
            );
            if (formControl && isFormArray(formControl)) {
                const formArray = formControl as UntypedFormArray;
                if (typeof subformIndex === 'number') {
                    const TOTAL_SUM = this.formProcessService.recalculate(
                        this.form,
                        field,
                        this.formDefinition?.conditions,
                        [subformIndex],
                    );
                    this.updateField(
                        {
                            name: field,
                            value: TOTAL_SUM,
                        },
                        subformIndex,
                        { source: 'recalculate' },
                    );
                } else {
                    for (let i = 0; i < formArray.length; i++) {
                        const TOTAL_SUM = this.formProcessService.recalculate(
                            this.form,
                            field,
                            this.formDefinition?.conditions,
                            [i],
                        );
                        this.updateField(
                            {
                                name: field,
                                value: TOTAL_SUM,
                            },
                            i,
                            { source: 'recalculate' },
                        );
                    }
                }
                return;
            }
        }

        const TOTAL_SUM = this.formProcessService.recalculate(
            this.form,
            field,
            this.formDefinition?.conditions,
        );
        this.updateField(
            {
                name: field,
                value: TOTAL_SUM,
            },
            undefined,
            { source: 'recalculate' },
        );
    }

    private updateSectionFromArray(sectionsArray: any): void {
        sectionsArray?.updateSections?.forEach((updateSection: any) =>
            this.updateSection(updateSection),
        );
    }

    private updateMessageDefinitionToShow(
        hideMessage?: ConditionalMessage[],
        showMessage?: ConditionalMessage[],
    ): void {
        if (hideMessage?.length) {
            hideMessage.forEach((removeMessage) => {
                delete this.formFieldsDataService.messageDefinitionToShow[
                    removeMessage.name as string
                ];
            });
        }

        if (showMessage?.length) {
            showMessage.forEach((addMessage) => {
                this.formFieldsDataService.messageDefinitionToShow[
                    addMessage.name as string
                ] =
                    this.formFieldsDataService.messageDefinitionToObject[
                        addMessage.name as string
                    ];
            });
        }
    }

    private updateSection(updateSectionConfig: UpdateSection): void {
        let count = 0;
        const layoutAndDefinition = this.layoutAndDefinition;
        const FINAL = layoutAndDefinition?.length as number;

        while (count < FINAL) {
            if (
                layoutAndDefinition?.[count].section?.name ===
                updateSectionConfig.name
            ) {
                layoutAndDefinition[count].section = {
                    ...layoutAndDefinition?.[count].section,
                    ...updateSectionConfig,
                    name:
                        updateSectionConfig.name ||
                        layoutAndDefinition?.[count].section?.name ||
                        '',
                };

                count = FINAL;
            }

            count++;
        }
        this.layoutAndDefinitionNext$(layoutAndDefinition);
    }

    private updateLabel(updateLabel: UpdateLabel): void {
        const layoutAndDefinition = this.layoutAndDefinition.map((section) => {
            return {
                ...section,
                layoutAndDefinition: section.layoutAndDefinition?.map(
                    (definition) => {
                        if (definition.name === updateLabel.name) {
                            return {
                                ...definition,
                                label: updateLabel.label,
                            };
                        }
                        return definition;
                    },
                ),
            };
        });

        this.layoutAndDefinitionNext$(layoutAndDefinition);
    }

    private updateValueFromOtherFields(updateField: UpdateField): UpdateField {
        const updatedField = { ...updateField };
        const value = updateField?.value;

        if (
            typeof value === 'string' &&
            value?.includes('{') &&
            value?.includes('}')
        ) {
            const formFieldsToCopyValuesFrom = value?.split(/[{}]/);
            let values = value;

            const formControl = this.formControlService.getFormControlData(
                this.form,
                updateField.name as string,
            )?.[0]?.formControl;

            if (
                formFieldsToCopyValuesFrom.filter((name) => name?.length > 0)
                    ?.length
            ) {
                formFieldsToCopyValuesFrom.forEach(
                    (otherFormFieldName: string) => {
                        if (otherFormFieldName.trim()) {
                            const newValue =
                                this.form?.value?.[otherFormFieldName];

                            values = values?.replaceAll(
                                `{${otherFormFieldName}}`,
                                newValue || '',
                            );

                            updatedField.value = values;
                        }
                    },
                );

                const formFieldType =
                    this.getLayoutAndDefinitionFromFormControl(formControl)
                        ?.fieldType as FormFieldType;

                this.formProcessService.findFormControlUpdateValue(
                    formControl as FormControl,
                    updateField.value,
                    formFieldType,
                    this.pristine,
                );
            }
        }

        return updatedField;
    }

    private updateField(
        updateField: UpdateFieldConfig,
        index: number | undefined,
        debugging: {
            trigger?: Trigger;
            functionName?: string;
            action?: Action;
            source?: string;
        },
    ): void {
        if (this.isDestroyed) {
            return;
        }
        this.updateFieldCtr++;
        let updateFieldConfig = { ...updateField };
        updateFieldConfig.correlationId = this.updateFieldCtr;
        if (this.updateFieldCtr % 1000 === 0) {
            console.error(
                new Date().toISOString(),
                `${this.formId} conditions have triggered over ${this.updateFieldCtr} form changes, conditions should be reviewed as to improve performance`,
            );
        }
        if (!updateFieldConfig?.name) {
            return;
        }

        const layoutAndDefinition = this.unsafeLayoutAndDefinition;
        const formControlsToUpdate: {
            formControl: AbstractControl;
            updateField: UpdateField;
            isAddressSubField?: boolean;
        }[] = [];
        let skipMakingChanges = false;
        layoutAndDefinition.forEach((sectionObject: ReactiveFormElement) => {
            let firstLevelCount = 0;
            const FIRST_LEVEL_LAYOUT_LENGTH =
                sectionObject.layoutAndDefinition?.length || 0;
            const UPDATE_FIELD_NAME =
                this.formControlService.stringToFormControlObject(
                    updateFieldConfig.name as string,
                );

            while (
                !skipMakingChanges &&
                firstLevelCount < FIRST_LEVEL_LAYOUT_LENGTH
            ) {
                const DEFINITION = sectionObject.layoutAndDefinition?.[
                    firstLevelCount
                ] as ReactiveFormLayoutAndDefinition;

                if (DEFINITION.name === UPDATE_FIELD_NAME.Level1ControlName) {
                    const FORM_CONTROLS_DATA =
                        this.formControlService.getFormControlData(
                            this.form,
                            updateFieldConfig.name as string,
                            typeof index === 'number' ? [index] : undefined,
                        );

                    if (
                        !UPDATE_FIELD_NAME.Level1HasSubForm &&
                        DEFINITION.fieldType === FormFieldType.Array &&
                        DEFINITION.childSchema &&
                        updateFieldConfig.isHidden
                    ) {
                        (
                            this.form.get(
                                UPDATE_FIELD_NAME.Level1ControlName,
                            ) as FormArray
                        ).clear();
                    }

                    // scenario: target is address child
                    if (
                        DEFINITION.fieldType === FormFieldType.Address &&
                        UPDATE_FIELD_NAME.Level2ControlName
                    ) {
                        const childIndex = DEFINITION.children?.findIndex(
                            (child) =>
                                child.name ===
                                UPDATE_FIELD_NAME.Level2ControlName,
                        );

                        const formControl =
                            FORM_CONTROLS_DATA?.[0]?.formControl;

                        if (
                            !formControl ||
                            childIndex === -1 ||
                            (
                                sectionObject.layoutAndDefinition as ReactiveFormLayoutAndDefinition[]
                            )[firstLevelCount]?.children?.length === undefined
                        ) {
                            skipMakingChanges = true;
                            break;
                        }

                        const childDef =
                            DEFINITION.children![childIndex as number];

                        (
                            sectionObject.layoutAndDefinition as ReactiveFormLayoutAndDefinition[]
                        )[firstLevelCount].children![childIndex as number] =
                            structuredClone({
                                ...childDef,
                                ...updateFieldConfig,
                                name: childDef.name,
                            }) as ReactiveFormElementChild;

                        firstLevelCount = FIRST_LEVEL_LAYOUT_LENGTH;

                        formControlsToUpdate.push({
                            formControl,
                            updateField: updateFieldConfig,
                            isAddressSubField: true,
                        });

                        break;
                    }

                    // scenario: subform update instigated from a non-subform control
                    if (UPDATE_FIELD_NAME.Level1HasSubForm && R.isNil(index)) {
                        const existingSubformInstances =
                            DEFINITION.children?.[0]?.layoutAndDefinition;
                        const childSchemaDefIndex =
                            DEFINITION?.childSchema?.findIndex(
                                ({ name }) =>
                                    name ===
                                    UPDATE_FIELD_NAME.Level2ControlName,
                            );

                        if (
                            existingSubformInstances?.length &&
                            FORM_CONTROLS_DATA?.length
                        ) {
                            if (R.isNil(UPDATE_FIELD_NAME.Level1SubFormIndex)) {
                                existingSubformInstances.map((_, i) => {
                                    this.updateField(
                                        updateFieldConfig,
                                        FORM_CONTROLS_DATA[i]?.index,
                                        debugging,
                                    );
                                });
                            } else if (
                                existingSubformInstances[
                                    UPDATE_FIELD_NAME.Level1SubFormIndex
                                ]
                            ) {
                                this.updateField(
                                    updateFieldConfig,
                                    UPDATE_FIELD_NAME.Level1SubFormIndex,
                                    debugging,
                                );
                            }
                        }

                        skipMakingChanges =
                            childSchemaDefIndex === -1 ||
                            Boolean(UPDATE_FIELD_NAME.Level2HasSubForm);
                        if (skipMakingChanges) {
                            break;
                        }

                        const target =
                            DEFINITION.childSchema?.[
                                childSchemaDefIndex as number
                            ];
                        if (this.noUpdatesRequired(target, updateFieldConfig)) {
                            skipMakingChanges = true;
                            break;
                        }

                        const updateFieldConfigWithSelectChanges =
                            this.selectControlUpdates(
                                target as ReactiveFormLayoutAndDefinition,
                                updateFieldConfig,
                            );

                        const updatedChildSchemaItem = new FieldDefinition({
                            ...target,
                            ...R.omit(
                                ['name'],
                                updateFieldConfigWithSelectChanges ??
                                    updateFieldConfig,
                            ),
                        } as any);

                        const childSchema = structuredClone(
                            DEFINITION.childSchema,
                        );
                        childSchema![childSchemaDefIndex as number] =
                            updatedChildSchemaItem;

                        (
                            sectionObject.layoutAndDefinition as ReactiveFormLayoutAndDefinition[]
                        )[firstLevelCount] = structuredClone({
                            ...DEFINITION,
                            ...{
                                childSchema,
                            },
                        }) as ReactiveFormLayoutAndDefinition;
                    }

                    // scenario: subform update instigated from a subform instance
                    // in this case we want to update only the specific subform instance and not the others
                    if (UPDATE_FIELD_NAME.Level1HasSubForm && !R.isNil(index)) {
                        const SUB_FORM_LAYOUT_AND_DEFINITION = DEFINITION
                            .children?.[0]?.layoutAndDefinition?.[
                            index as number
                        ] as unknown as ReactiveFormLayoutAndDefinition[];

                        // check if any changes need to be applied
                        if (
                            !UPDATE_FIELD_NAME.Level2HasSubForm &&
                            SUB_FORM_LAYOUT_AND_DEFINITION
                        ) {
                            const target = SUB_FORM_LAYOUT_AND_DEFINITION.find(
                                (x) =>
                                    x.name ===
                                    UPDATE_FIELD_NAME.Level2ControlName,
                            );

                            if (target) {
                                updateFieldConfig =
                                    this.removeUpdatesThatAreBlockedByLocks(
                                        updateFieldConfig,
                                        target,
                                    );
                                if (
                                    this.noUpdatesRequired(
                                        target,
                                        updateFieldConfig,
                                    )
                                ) {
                                    skipMakingChanges = true;
                                    break;
                                }
                            }
                        }

                        let level3FormDefIndex;

                        const subFormIndex =
                            SUB_FORM_LAYOUT_AND_DEFINITION.findIndex(
                                (x) =>
                                    x.name ===
                                    UPDATE_FIELD_NAME.Level2ControlName,
                            );

                        const SUB_DEFINITION =
                            SUB_FORM_LAYOUT_AND_DEFINITION[subFormIndex];

                        if (SUB_DEFINITION) {
                            let subFormControlData = FORM_CONTROLS_DATA;
                            let subFormDefinition = SUB_DEFINITION;
                            const level2IsAddress =
                                subFormDefinition.fieldType ===
                                FormFieldType.Address;

                            if (
                                UPDATE_FIELD_NAME.Level2HasSubForm &&
                                UPDATE_FIELD_NAME.Level3ControlName
                            ) {
                                const level2FormData:
                                    | IFormControlData
                                    | undefined = subFormControlData.find(
                                    ({ formControl }) =>
                                        getFormControlType(formControl) ===
                                        FormGroup,
                                );
                                const level3FormControl = (
                                    level2FormData?.formControl as FormGroup
                                )?.controls[
                                    UPDATE_FIELD_NAME.Level3ControlName
                                ];

                                level3FormDefIndex = level2IsAddress
                                    ? subFormDefinition?.children?.findIndex(
                                          (cs) =>
                                              cs.name ===
                                              UPDATE_FIELD_NAME.Level3ControlName,
                                      )
                                    : subFormDefinition?.childSchema?.findIndex(
                                          (cs) =>
                                              cs.name ===
                                              UPDATE_FIELD_NAME.Level3ControlName,
                                      );

                                if (
                                    level3FormControl &&
                                    level2FormData &&
                                    R.isNotNil(level3FormDefIndex) &&
                                    level3FormDefIndex > -1
                                ) {
                                    const level3FormDef = level2IsAddress
                                        ? subFormDefinition?.children?.[
                                              level3FormDefIndex
                                          ]
                                        : subFormDefinition?.childSchema?.[
                                              level3FormDefIndex
                                          ];
                                    subFormDefinition =
                                        level3FormDef as ReactiveFormLayoutAndDefinition;
                                    subFormControlData = [
                                        {
                                            ...level2FormData,
                                            formControl: level3FormControl,
                                        },
                                    ];
                                } else {
                                    return;
                                }
                            }

                            subFormControlData.forEach(({ formControl }) => {
                                if (!formControl) {
                                    return;
                                }
                                if (
                                    SUB_DEFINITION.isHidden &&
                                    Object.hasOwn(
                                        updateFieldConfig,
                                        'isHidden',
                                    ) &&
                                    !updateFieldConfig.isHidden
                                ) {
                                    updateFieldConfig.unHiding = true;
                                }
                                formControlsToUpdate.push({
                                    formControl,
                                    updateField: updateFieldConfig,
                                });
                            });

                            const updateFieldConfigWithSelectChanges =
                                this.selectControlUpdates(
                                    SUB_DEFINITION,
                                    updateFieldConfig,
                                );

                            const currencyControlOverrides =
                                this.currencyControlUpdates(
                                    SUB_DEFINITION,
                                    updateFieldConfig,
                                );

                            const updatedDef = structuredClone({
                                ...subFormDefinition,
                                ...(updateFieldConfigWithSelectChanges ??
                                    updateFieldConfig),
                                ...currencyControlOverrides,
                                name: subFormDefinition.name,
                            }) as ReactiveFormLayoutAndDefinition;

                            if (
                                R.isNotNil(level3FormDefIndex) &&
                                level3FormDefIndex > -1
                            ) {
                                if (level2IsAddress) {
                                    SUB_FORM_LAYOUT_AND_DEFINITION[
                                        subFormIndex
                                    ].children![level3FormDefIndex] =
                                        updatedDef as ReactiveFormElementChild;
                                } else {
                                    SUB_FORM_LAYOUT_AND_DEFINITION[
                                        subFormIndex
                                    ].childSchema![level3FormDefIndex] =
                                        updatedDef;
                                }
                            } else {
                                SUB_FORM_LAYOUT_AND_DEFINITION[subFormIndex] =
                                    updatedDef;
                            }
                        }
                    }

                    // scenario: root level field being updated
                    if (!UPDATE_FIELD_NAME.Level1HasSubForm) {
                        updateFieldConfig =
                            this.removeUpdatesThatAreBlockedByLocks(
                                updateFieldConfig,
                                DEFINITION,
                            );
                        if (
                            this.noUpdatesRequired(
                                DEFINITION,
                                updateFieldConfig,
                            )
                        ) {
                            {
                                skipMakingChanges = true;
                                break;
                            }
                        }

                        let addressChildrenObject:
                            | {
                                  children?: ReactiveFormElementChild[];
                              }
                            | undefined;

                        // scenario: address children being updated as we have childSchema or options.
                        // note: options are applied to addressType child control
                        if (
                            (updateFieldConfig?.childSchema ||
                                updateFieldConfig?.options) &&
                            DEFINITION.fieldType === FormFieldType.Address &&
                            DEFINITION?.children
                        ) {
                            const {
                                childrenObject,
                                addressFormControlsToUpdate,
                            } = this.updateAddressChildSchema(
                                updateFieldConfig,
                                DEFINITION,
                            );
                            addressChildrenObject = childrenObject.children
                                ? childrenObject
                                : addressChildrenObject;

                            addressFormControlsToUpdate?.forEach(
                                (controlToUpdate) => {
                                    formControlsToUpdate.push(controlToUpdate);
                                },
                            );
                        }

                        const updateFieldConfigWithSelectChanges =
                            this.selectControlUpdates(
                                DEFINITION,
                                updateFieldConfig,
                            );

                        const currencyControlOverrides =
                            this.currencyControlUpdates(
                                DEFINITION,
                                updateFieldConfig,
                            );

                        (
                            sectionObject.layoutAndDefinition as ReactiveFormLayoutAndDefinition[]
                        )[firstLevelCount] = structuredClone({
                            ...DEFINITION,
                            ...(updateFieldConfigWithSelectChanges ??
                                updateFieldConfig),
                            ...(addressChildrenObject ?? {}),
                            ...currencyControlOverrides,
                            name: DEFINITION.name,
                        }) as ReactiveFormLayoutAndDefinition;

                        firstLevelCount = FIRST_LEVEL_LAYOUT_LENGTH;

                        FORM_CONTROLS_DATA.forEach(({ formControl }) => {
                            if (!formControl) {
                                return;
                            }
                            if (
                                DEFINITION.isHidden &&
                                Object.hasOwn(updateFieldConfig, 'isHidden') &&
                                !updateFieldConfig.isHidden
                            ) {
                                updateFieldConfig.unHiding = true;
                            }
                            formControlsToUpdate.push({
                                formControl,
                                updateField: updateFieldConfig,
                            });
                        });
                    }

                    // exit the while loop as we have found the target field and applied changes
                    break;
                }

                firstLevelCount++;
            }
        });

        if (skipMakingChanges) {
            return;
        }

        if (this.debugConditions) {
            this.updateFieldDebugging(updateFieldConfig, index, debugging);
        }

        this.layoutAndDefinitionNext$(layoutAndDefinition);
        formControlsToUpdate.forEach(
            ({ formControl, updateField, isAddressSubField }) => {
                if (!formControl) {
                    return;
                }
                if (isAddressSubField) {
                    this.updateAddressSubFieldFormControl(
                        formControl,
                        updateField,
                    );
                } else {
                    this.updateFieldFormControl(
                        formControl,
                        updateField,
                        false,
                        index,
                    );
                }
            },
        );
    }

    private updateAddressSubFieldFormControl(
        formControl: AbstractControl,
        updateField: UpdateFieldConfig,
    ) {
        if (!formControl?.parent) {
            return;
        }

        // skip validation changes as this handled in the FormAddressControlComponent - it's done there as we
        // want to enable validations for address children only if the address is required or a field has a value
        this.updateFieldFormControl(formControl, updateField, true);
    }

    private updateFieldFormControl(
        formControl: AbstractControl,
        updateField: UpdateFieldConfig,
        skipValidationChanges?: boolean,
        index?: number,
    ) {
        const config = {
            ...this.getLayoutAndDefinitionFromFormControl(formControl),
            ...updateField,
        };
        if (!config) {
            console.error(
                'unable to find config for formcontrol',
                updateField.name,
                this.layoutAndDefinition,
            );
            return;
        }

        const valueOnlyUpdate = Object.keys(updateField).every((key) =>
            ['name', 'value'].includes(key),
        );
        let valueUpdated = false;

        if (updateField.unHiding) {
            this.formTriggersService
                .findTriggersThatWatchField(
                    this.formDefinition.conditions?.triggers,
                    updateField.name as string,
                )
                ?.forEach((trigger) => {
                    this.applyConditions(trigger, { formControl, index });
                });
        }

        if (updateField.value !== undefined) {
            valueUpdated = this.formProcessService.findFormControlUpdateValue(
                formControl as FormControl,
                updateField.value,
                config?.fieldType as FormFieldType,
                this.pristine,
            );
            if (valueUpdated) {
                this.fieldValueUpdated$.next(updateField.name as string);
            }
        }

        if (
            (valueOnlyUpdate && !valueUpdated) ||
            skipValidationChanges ||
            this.formLockService.fieldIsLocked(
                updateField.name as string,
                this.formData?.lock as FormLock,
            )
        ) {
            return;
        }

        this.formProcessService.updateFormControlValidation(
            formControl as UntypedFormControl,
            new FieldDefinition(config as any),
            new FormElementLayoutDefinition(config as any),
        );
    }

    private layoutAndDefinitionNext$(
        layoutAndDefinition: LayoutAndDefinitionEntry[],
    ): void {
        this._layoutAndDefinition$.next(layoutAndDefinition);
    }

    private isSelectWithSelectSource(
        fieldDefinition: ReactiveFormLayoutAndDefinition,
    ) {
        return (
            fieldDefinition.fieldType === FormFieldType.Select &&
            fieldDefinition.selectSource
        );
    }

    private calculateSelectControlOptionsConfig(
        fieldDefinition: ReactiveFormLayoutAndDefinition,
        updateField: UpdateField,
    ) {
        const isSelect = fieldDefinition.fieldType === FormFieldType.Select;
        const hasOptions = !!updateField.options?.length;

        if (!isSelect || !hasOptions) {
            return {};
        }
        if (updateField.options!.some((option) => typeof option !== 'string')) {
            return { options: updateField.options };
        }

        const { selectSource, originalOptions, options } = fieldDefinition;

        if (selectSource) {
            return { optionsWhitelist: updateField.options };
        }

        const defOptions = originalOptions ?? options;
        const newOptions = updateField.options;

        return {
            options:
                defOptions?.filter((v) =>
                    (newOptions as string[]).includes(v.value),
                ) ?? [],
        };
    }

    private getContextChanges(
        updateField: UpdateField,
    ): Partial<ReactiveFormLayoutAndDefinition> {
        const fieldDefChanges: Partial<ReactiveFormLayoutAndDefinition> = {};
        if (updateField.clearContext) {
            fieldDefChanges.contextualValues = null;
        }

        if (updateField.setContext) {
            fieldDefChanges.contextualValues = updateField.setContext;
        }

        return fieldDefChanges;
    }

    private updateAddressChildSchema(
        updateFieldConfig: UpdateField & Pick<FieldDefinition, 'childSchema'>,
        definition: ReactiveFormLayoutAndDefinition,
    ) {
        const childrenObject: {
            children?: ReactiveFormElementChild[];
        } = definition?.children ? { children: definition.children } : {};
        const addressFormControlsToUpdate: {
            formControl: AbstractControl;
            updateField: UpdateField;
            isAddressSubField?: boolean;
        }[] = [];
        const addressFormGroup = this.formControlService.getFormControlData(
            this.form,
            updateFieldConfig.name as string,
        )?.[0]?.formControl as FormGroup;

        if (addressFormGroup?.controls) {
            updateFieldConfig.childSchema?.forEach((child) => {
                const updateField = child as UpdateField;
                const childFormControl =
                    addressFormGroup?.controls?.[child?.name as string];

                addressFormControlsToUpdate.push({
                    formControl: childFormControl,
                    updateField,
                    isAddressSubField: true,
                });
            });
        }

        const childSchemaUpdates = (updateFieldConfig.childSchema ??
            []) as ReactiveFormElementChild[];

        // due to api limitations we need set addressType field options from the parent address control
        // see address-type.service.ts where this is done for initial address setup
        if (Object.hasOwn(updateFieldConfig, 'options')) {
            const addressTypeIndex = childSchemaUpdates.findIndex(
                (cs) => cs.name === ADDRESS_CONTROL_ADDRESS_TYPE,
            );
            if (addressTypeIndex > -1) {
                childSchemaUpdates[addressTypeIndex].options =
                    updateFieldConfig.options;
            } else {
                childSchemaUpdates.push({
                    name: ADDRESS_CONTROL_ADDRESS_TYPE,
                    options: updateFieldConfig.options,
                });
            }
        }

        childSchemaUpdates?.forEach((child) => {
            const targetIndex = childrenObject.children!.findIndex(
                (x) => x.name === child.name,
            );
            if (targetIndex > -1) {
                childrenObject.children![targetIndex] = {
                    ...childrenObject.children![targetIndex],
                    ...child,
                };
            }
        });

        return { addressFormControlsToUpdate, childrenObject };
    }

    private currencyControlUpdates(
        fieldDefinition: ReactiveFormLayoutAndDefinition,
        updateFieldConfig: UpdateField,
    ) {
        if (
            fieldDefinition.fieldType !== FormFieldType.Currency ||
            !updateFieldConfig.options?.length
        ) {
            return {};
        }
        let options = updateFieldConfig.options;
        if (
            options?.length &&
            options.some(
                (option) =>
                    typeof option === 'string' &&
                    this.formStaticService.isValueFromDataContext(option),
            )
        ) {
            options = options.map((option) =>
                this.formStaticService.isValueFromDataContext(option)
                    ? this.formStaticService.getValueFromFormDataContext(option)
                    : option,
            ) as string[];
        }
        return { options };
    }

    private selectControlUpdates(
        fieldDefinition: ReactiveFormLayoutAndDefinition,
        updateFieldConfig: UpdateField,
    ): ReactiveFormLayoutAndDefinition | undefined {
        if (
            !fieldDefinition.fieldType ||
            ![FormFieldType.Select, FormFieldType.Multiselect].includes(
                fieldDefinition.fieldType,
            )
        ) {
            return;
        }

        const selectWithSource = this.isSelectWithSelectSource(fieldDefinition);
        const selectControlSpecificConfig =
            this.calculateSelectControlOptionsConfig(
                fieldDefinition,
                updateFieldConfig,
            );

        const contextConfig = this.getContextChanges(updateFieldConfig);
        const cleanedUpdateField = selectWithSource
            ? R.omit(['options'], updateFieldConfig)
            : updateFieldConfig;

        return {
            ...cleanedUpdateField,
            ...contextConfig,
            ...selectControlSpecificConfig,
        } as ReactiveFormLayoutAndDefinition;
    }

    private removeUpdatesThatAreBlockedByLocks(
        updateFieldConfig: UpdateFieldConfig,
        definition: ReactiveFormLayoutAndDefinition,
    ): UpdateFieldConfig {
        const updatedConfig = { ...updateFieldConfig };
        if (this.blockedVisibilityUpdate(updateFieldConfig, definition)) {
            delete updatedConfig.isHidden;
        }
        if (this.blockedMandatoryUpdate(updateFieldConfig, definition)) {
            delete updatedConfig.isRequired;
        }
        return updatedConfig;
    }

    private noUpdatesRequired(
        target: FieldDefinition | undefined,
        updateFieldConfig: UpdateField & Pick<FieldDefinition, 'childSchema'>,
    ): boolean {
        if (Object.hasOwn(updateFieldConfig, 'value')) {
            return false;
        }
        return Object.entries(updateFieldConfig ?? {})
            .filter(([key]) => key !== 'name')
            .every(([key, value]) => (target as any)[key] === value);
    }

    private blockedVisibilityUpdate(
        updateFieldConfig: UpdateFieldConfig,
        definition: ReactiveFormLayoutAndDefinition,
    ): boolean {
        if (
            !Object.hasOwn(updateFieldConfig, 'isHidden') ||
            !definition.visibilityLocked ||
            updateFieldConfig.visibilityLockOverride
        ) {
            return false;
        }
        return true;
    }

    private blockedMandatoryUpdate(
        updateFieldConfig: UpdateFieldConfig,
        definition: ReactiveFormLayoutAndDefinition,
    ): boolean {
        if (
            !Object.hasOwn(updateFieldConfig, 'isRequired') ||
            !definition.mandatoryLocked ||
            updateFieldConfig.mandatoryLockOverride
        ) {
            return false;
        }
        return true;
    }

    private updateFieldDebugging(
        updateFieldConfig: UpdateField & Pick<FieldDefinition, 'childSchema'>,
        index: number | undefined,
        debugging: {
            trigger?: Trigger;
            functionName?: string;
            action?: Action;
            source?: string;
        },
    ) {
        if (debugging.trigger) {
            console.log(
                `form ${updateFieldConfig.name} update: `,
                updateFieldConfig,
                '\ntriggered by:',
                debugging.trigger,
                '\nsubform index:',
                index,
            );
        } else if (debugging.functionName) {
            console.log(
                `form ${updateFieldConfig.name} update: `,
                updateFieldConfig,
                '\nfunction:',
                debugging.functionName,
            );
        } else if (debugging.source) {
            console.log(
                `form ${updateFieldConfig.name} update: `,
                updateFieldConfig,
                '\nsource:',
                debugging.source,
            );
        } else if (debugging.action) {
            console.log(
                `form ${updateFieldConfig.name} update: `,
                updateFieldConfig,
                '\naction:',
                debugging.action,
            );
        } else {
            console.log(
                `form ${updateFieldConfig.name} update: `,
                updateFieldConfig,
                '\nunknown',
                debugging,
            );
        }
    }
}
